<template>
    <div>
        <label
            :for="inputName"
            :class="[
                'inline-flex items-start gap-2 p-1 select-none',
                local.isDisabled ? config.disabled : 'cursor-pointer',
            ]"
        >
            <input
                :id="inputName"
                v-model="model"
                type="checkbox"
                :name="inputName"
                class="peer sr-only"
                :disabled="local.isDisabled"
            />
            <div
                :class="[
                    config.state[local.state].colors,
                    'peer relative h-5 w-9 rounded-full peer-focus:ring-2 after:absolute after:start-[2px] after:top-0.5 after:h-4 after:w-4 after:rounded-full after:border after:transition-all after:content-[\'\'] peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full',
                ]"
            ></div>
            <div class="flex flex-col items-start justify-center gap-1">
                <span
                    v-if="local.label.length > 0"
                    :class="[config.state[local.state].label.default, 'text-sm font-medium']"
                >
                    {{ local.label }}
                </span>
                <span
                    v-if="model && local.labelOn.length > 0"
                    :class="[config.state[local.state].label.on, 'text-sm font-medium']"
                >
                    {{ local.labelOn }}
                </span>
                <span
                    v-if="!model && local.labelOff.length > 0"
                    :class="[config.state[local.state].label.off, 'text-sm font-medium']"
                >
                    {{ local.labelOff }}
                </span>
            </div>
        </label>
    </div>
</template>
<script setup>
import { watch, reactive, computed } from 'vue';

defineExpose({
    name: 'SwitchButton',
});

const props = defineProps({
    modelValue: {
        type: Boolean,
    },
    state: {
        type: String,
        default: 'neutral',
    },
    label: {
        type: String,
        default: '',
    },
    labelOn: {
        type: String,
        default: '',
    },
    labelOff: {
        type: String,
        default: '',
    },
    isDisabled: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits(['update:modelValue']);

const model = computed({
    get: () => props.modelValue,
    set: (value) => emit('update:modelValue', value),
});

const local = reactive({
    state: props.state,
    label: props.label,
    labelOn: props.labelOn,
    labelOff: props.labelOff,
    isDisabled: props.isDisabled,
});

const inputName = `switch-${Math.random().toString(36).substring(2, 15)}`;

watch(
    () => props.state,
    (newValue) => {
        local.state = newValue;
    },
);

const config = {
    disabled: 'opacity-50 cursor-not-allowed',
    state: {
        brand: {
            colors: 'bg-indigo-300 peer-focus:ring-indigo-300 dark:peer-focus:ring-indigo-700 dark:bg-indigo-700 peer-checked:after:border-indigo-200 after:bg-indigo-300 after:border-indigo-200 dark:border-indigo-600 peer-checked:bg-indigo-500',
            label: {
                default: 'text-gray-700 dark:text-gray-300',
                on: 'text-indigo-700 dark:text-indigo-300',
                off: 'text-gray-700 dark:text-gray-300',
            },
        },
        promo: {
            colors: 'bg-violet-300 peer-focus:ring-violet-300 dark:peer-focus:ring-violet-800 dark:bg-violet-700 peer-checked:after:border-white after:bg-white after:border-violet-300 dark:border-violet-600 peer-checked:bg-violet-500',
            label: {
                default: 'text-gray-700 dark:text-gray-300',
                on: 'text-violet-700 dark:text-violet-300',
                off: 'text-gray-700 dark:text-gray-300',
            },
        },
        primary: {
            colors: 'bg-blue-300 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 dark:bg-blue-700 peer-checked:after:border-white after:bg-white after:border-blue-300 dark:border-blue-600 peer-checked:bg-blue-500',
            label: {
                default: 'text-gray-700 dark:text-gray-300',
                on: 'text-blue-700 dark:text-blue-300',
                off: 'text-gray-700 dark:text-gray-300',
            },
        },
        info: {
            colors: 'bg-cyan-300 peer-focus:ring-cyan-300 dark:peer-focus:ring-cyan-800 dark:bg-cyan-700 peer-checked:after:border-white after:bg-white after:border-cyan-300 dark:border-cyan-600 peer-checked:bg-cyan-500',
            label: {
                default: 'text-gray-700 dark:text-gray-300',
                on: 'text-cyan-700 dark:text-cyan-300',
                off: 'text-gray-700 dark:text-gray-300',
            },
        },
        success: {
            colors: 'bg-emerald-300 peer-focus:ring-emerald-300 dark:peer-focus:ring-emerald-800 dark:bg-emerald-700 peer-checked:after:border-white after:bg-white after:border-emerald-300 dark:border-emerald-600 peer-checked:bg-emerald-500',
            label: {
                default: 'text-gray-700 dark:text-gray-300',
                on: 'text-emerald-700 dark:text-emerald-300',
                off: 'text-gray-700 dark:text-gray-300',
            },
        },
        warning: {
            colors: 'bg-amber-300 peer-focus:ring-amber-300 dark:peer-focus:ring-amber-800 dark:bg-amber-700 peer-checked:after:border-white after:bg-white after:border-amber-300 dark:border-amber-600 peer-checked:bg-amber-500',
            label: {
                default: 'text-gray-700 dark:text-gray-300',
                on: 'text-amber-700 dark:text-amber-300',
                off: 'text-gray-700 dark:text-gray-300',
            },
        },
        danger: {
            colors: 'bg-red-200 peer-focus:ring-red-300 dark:peer-focus:ring-red-800 dark:bg-red-700 peer-checked:after:border-white after:bg-white after:border-red-300 dark:border-red-600 peer-checked:bg-red-500',
            label: {
                default: 'text-gray-700 dark:text-gray-300',
                on: 'text-red-700 dark:text-red-300',
                off: 'text-gray-700 dark:text-gray-300',
            },
        },
        neutral: {
            colors: 'bg-gray-300 peer-focus:ring-gray-300 dark:peer-focus:ring-gray-800 dark:bg-gray-700 peer-checked:after:border-white after:bg-white after:border-gray-300 dark:border-gray-600 peer-checked:bg-gray-500',
            label: {
                default: 'text-gray-700 dark:text-gray-300',
                on: 'text-gray-700 dark:text-gray-300',
                off: 'text-gray-700 dark:text-gray-300',
            },
        },
    },
};
</script>
